/* IntaSend payments are enabled only after the server connection is verified. */
(() => {
  const endpointFor = element => {
    const raw = element.dataset.endpoint;
    if (!raw || element.dataset.enabled !== 'true') return null;
    try {
      const url = new URL(raw);
      return url.protocol === 'https:' && url.hostname === 'webhooks.getmesa.com' ? url.href : null;
    } catch { return null; }
  };
  const request = async (endpoint, body) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 55000);
    try {
      // text/plain avoids a preflight; the server strictly parses and validates JSON.
      const response = await fetch(endpoint, {
        method: 'POST', credentials: 'omit', cache: 'no-store',
        headers: {'Content-Type': 'text/plain;charset=UTF-8', Accept: 'application/json'},
        body: JSON.stringify(body), signal: controller.signal
      });
      if (!response.ok) throw new Error('We could not reach the payment service. Please try again.');
      const result = await response.json();
      if (!result || typeof result.ok !== 'boolean') throw new Error('We could not confirm the response. Please try again.');
      if (!result.ok) throw new Error(result.error?.message || 'We could not confirm the request. Please try again.');
      return result;
    } finally { clearTimeout(timeout); }
  };
  const format = minor => 'KSh ' + (minor / 100).toLocaleString('en-KE', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  const initialise = root => {
    root.querySelectorAll('eas-kenya-payment-choice').forEach(element => {
      if (element.dataset.paymentBound) return;
      const endpoint = endpointFor(element);
      const submit = element.querySelector('.eas-ke-continue');
      if (!endpoint || !submit) return;
      element.dataset.paymentBound = 'true';
      const name = element.querySelector('[data-eas-name]');
      const phone = element.querySelector('[data-eas-phone]');
      const address = element.querySelector('[data-eas-address]');
      const message = element.querySelector('[data-eas-message]');
      const quantity = element.querySelector('[data-eas-quantity]');
      let pending = false, reference = null, fingerprint = null;
      submit.disabled = false;
      submit.addEventListener('click', async () => {
        if (pending) return;
        const fields = [name, phone, address];
        for (const field of fields) {
          field.setCustomValidity('');
          if (!field.value.trim()) field.setCustomValidity('Please complete this field.');
          if (!field.reportValidity()) return;
        }
        const mobile = phone.value.replace(/[\s()+-]/g, '').replace(/^0/, '254');
        if (!/^254[17]\d{8}$/.test(mobile)) {
          phone.setCustomValidity('Enter your Kenyan M-Pesa mobile number, for example 0712 345 678.');
          phone.reportValidity(); return;
        }
        const qty = Number(quantity.value), unit = Number(element.dataset.price);
        if (!Number.isSafeInteger(unit) || unit <= 0 || !Number.isInteger(qty) || qty < 1 || qty > 10) return;
        const payload = {action: 'create', country: 'KE', variant_id: element.dataset.variant,
          quantity: qty, name: name.value.trim(), phone: mobile, address: address.value.trim(),
          expected_total_minor: unit * qty - Math.round(unit * qty / 10)};
        const current = JSON.stringify(payload);
        // Keep the same capability for retries, including a lost server response.
        if (!reference || current !== fingerprint) {
          reference = Array.from(crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
          fingerprint = current;
        }
        payload.attempt_id = reference;
        pending = true; submit.disabled = true; submit.setAttribute('aria-busy', 'true');
        const originalLabel = submit.textContent;
        submit.textContent = 'Preparing secure payment…';
        message.textContent = 'Preparing your ' + format(payload.expected_total_minor) + ' payment. Please keep this page open.';
        try {
          const result = await request(endpoint, payload);
          if (result.state === 'paid') {
            location.assign('/pages/payment-status#' + reference); return;
          }
          const checkout = new URL(result.checkout_url);
          if (checkout.protocol !== 'https:' || !['payment.intasend.com', 'sandbox.intasend.com'].includes(checkout.hostname) || !checkout.pathname.startsWith('/checkout/')) {
            throw new Error('We could not verify your payment link. Please try again.');
          }
          location.assign(checkout.href);
        } catch (error) {
          message.textContent = error.name === 'AbortError'
            ? 'The connection took too long. Please retry here; your existing payment reference will be reused.'
            : error.message;
        } finally {
          pending = false; submit.disabled = false; submit.removeAttribute('aria-busy'); submit.textContent = originalLabel;
        }
      });
    });
    root.querySelectorAll('[data-eas-payment-status]').forEach(element => {
      if (element.dataset.paymentBound) return;
      const endpoint = endpointFor(element), reference = location.hash.slice(1);
      const heading = element.querySelector('[data-eas-status-heading]');
      const message = element.querySelector('[data-eas-status-message]');
      const button = element.querySelector('[data-eas-check-payment]');
      if (!endpoint) return;
      element.dataset.paymentBound = 'true';
      if (!/^[a-f0-9]{32}$/.test(reference)) {
        heading.textContent = 'Payment reference missing';
        message.textContent = 'Return using the link provided after your IntaSend payment, or contact us with your payment receipt.';
        return;
      }
      let busy = false, done = false, timer;
      const check = async () => {
        if (busy || done) return;
        busy = true; button.disabled = true; button.hidden = false;
        try {
          const result = await request(endpoint, {action: 'status', attempt_id: reference});
          if (result.state === 'paid') {
            done = true; clearTimeout(timer);
            heading.textContent = 'Thank you. Your order is confirmed.';
            message.textContent = 'Order ' + (result.order_name || '') + ' · M-Pesa payment received: KSh ' + result.amount + '. Delivery is free.';
            button.hidden = true;
          } else {
            heading.textContent = result.state === 'confirming_order' ? 'Payment received. Confirming your order…' : 'Waiting for payment confirmation';
            message.textContent = 'If you have paid, please do not pay again or place another order. You can check the confirmation again below.';
          }
        } catch {
          heading.textContent = 'We are checking your payment';
          message.textContent = 'We could not refresh the confirmation. If you have paid, do not pay again. Try checking once more or contact us with your receipt.';
        } finally {
          busy = false;
          // Bounded requests avoid an ongoing stream of billable MESA tasks.
          timer = setTimeout(() => { button.disabled = false; }, 20000);
        }
      };
      button.addEventListener('click', check);
      check();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise(document), {once:true});
  else initialise(document);
  document.addEventListener('shopify:section:load', event => initialise(event.target));
})();
